import { useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Check, ChevronsUpDown, Loader2, ArrowRight } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  useTripSearch,
  useMoveInvoice,
  useMoveInvoiceToNewTrip,
} from "@/hooks/useInvoices";
import NewTripFields, {
  EMPTY_NEW_TRIP,
  type NewTripData,
} from "@/components/invoicing/NewTripFields";
import { useToast } from "@/hooks/use-toast";
import type { Invoice, Trip } from "@shared/schema";
import { formatTramsInvoiceDisplayNumber, getTramsInvoiceNumberFromMetadata } from "@shared/invoice-display";

type Mode = "existing" | "new";

interface MoveInvoiceDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  invoice: Pick<
    Invoice,
    | "id"
    | "invoiceNumber"
    | "metadata"
    | "tripId"
    | "recordLocator"
    | "invoiceStatusCode"
    | "status"
  >;
  /**
   * Optional callback fired once the move (and optional inline trip-create)
   * completes successfully. Receives the new tripId + recordLocator.
   */
  onMoved?: (result: { newTripId: number; newRecordLocator: string | null }) => void;
}

export default function MoveInvoiceDialog({
  open,
  onOpenChange,
  invoice,
  onMoved,
}: MoveInvoiceDialogProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const moveMutation = useMoveInvoice();
  const moveToNewTripMutation = useMoveInvoiceToNewTrip();

  const [mode, setMode] = useState<Mode>("existing");
  const [tripSearch, setTripSearch] = useState("");
  const [tripPopoverOpen, setTripPopoverOpen] = useState(false);
  const [selectedTrip, setSelectedTrip] = useState<Trip | null>(null);
  const [reason, setReason] = useState("");

  const [newTrip, setNewTrip] = useState<NewTripData>(EMPTY_NEW_TRIP);
  const updateNewTrip = (patch: Partial<NewTripData>) =>
    setNewTrip((prev) => ({ ...prev, ...patch }));

  const { data: trips = [] } = useTripSearch(tripSearch);

  const filteredTrips = useMemo(
    () => trips.filter((t) => t.id !== invoice.tripId),
    [trips, invoice.tripId],
  );

  const isMovable =
    invoice.invoiceStatusCode === "O" && invoice.status !== "voided";

  const reset = () => {
    setMode("existing");
    setTripSearch("");
    setTripPopoverOpen(false);
    setSelectedTrip(null);
    setReason("");
    setNewTrip(EMPTY_NEW_TRIP);
  };

  const isSubmitting =
    moveMutation.isPending || moveToNewTripMutation.isPending;

  const handleClose = (next: boolean) => {
    if (!next && isSubmitting) return;
    if (!next) reset();
    onOpenChange(next);
  };

  const performMove = async (targetTripId: number) => {
    try {
      const result = await moveMutation.mutateAsync({
        id: invoice.id,
        targetTripId,
        sourceTripId: invoice.tripId ?? undefined,
        reason: reason.trim() || undefined,
        operationId: `move-${invoice.id}-${Date.now()}`,
      });
      toast({
        title: "Invoice moved",
        description: `Trip # updated to ${
          result.invoice.recordLocator ?? "n/a"
        }.`,
      });
      onMoved?.({
        newTripId: result.invoice.tripId,
        newRecordLocator: result.invoice.recordLocator ?? null,
      });
      handleClose(false);
    } catch (error) {
      toast({
        title: "Move failed",
        description:
          error instanceof Error ? error.message : "Unable to move invoice.",
        variant: "destructive",
      });
    }
  };

  const handleSubmit = async () => {
    if (!isMovable) return;

    if (mode === "existing") {
      if (!selectedTrip) {
        toast({
          title: "Pick a trip",
          description: "Select a trip before moving.",
          variant: "destructive",
        });
        return;
      }
      await performMove(selectedTrip.id);
      return;
    }

    if (!newTrip.name.trim()) {
      toast({
        title: "Trip name required",
        description: "Enter a name for the new trip.",
        variant: "destructive",
      });
      return;
    }
    if (newTrip.destinations.length === 0) {
      toast({
        title: "Destinations required",
        description: "Add at least one destination for the new trip.",
        variant: "destructive",
      });
      return;
    }
    if (newTrip.isGroup && !newTrip.groupName.trim()) {
      toast({
        title: "Group booking number required",
        description: "Enter the group booking number for this trip.",
        variant: "destructive",
      });
      return;
    }

    try {
      const result = await moveToNewTripMutation.mutateAsync({
        id: invoice.id,
        sourceTripId: invoice.tripId ?? undefined,
        trip: {
          name: newTrip.name.trim(),
          destinations: newTrip.destinations.map((d) => ({
            id: d.id,
            displayName: d.displayName,
            destinationType: d.destinationType,
            parentRegion: d.parentRegion ?? null,
          })),
          isGroup: newTrip.isGroup,
          groupName: newTrip.isGroup ? newTrip.groupName.trim() : undefined,
        },
        reason: reason.trim() || undefined,
        operationId: `move-new-${invoice.id}-${Date.now()}`,
      });
      queryClient.invalidateQueries({ queryKey: ["/api/trips/mine"] });
      toast({
        title: "Invoice moved",
        description: `Trip ${result.trip.recordLocator ?? `#${result.trip.id}`} created. Trip # updated to ${
          result.invoice.recordLocator ?? "n/a"
        }.`,
      });
      onMoved?.({
        newTripId: result.invoice.tripId,
        newRecordLocator: result.invoice.recordLocator ?? null,
      });
      handleClose(false);
    } catch (error) {
      toast({
        title: "Move failed",
        description:
          error instanceof Error
            ? error.message
            : "Unable to move invoice to a new trip.",
        variant: "destructive",
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[520px] flex max-h-[90vh] flex-col gap-0 p-0">
        <DialogHeader className="border-b px-6 pb-4 pt-6">
          <DialogTitle>Move Invoice to a Different Trip</DialogTitle>
          <DialogDescription>
            Move this invoice to a different trip.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 space-y-4 overflow-y-auto px-6 py-4">

        {!isMovable && (
          <div className="rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">
            Only Open invoices can be moved. Current status:{" "}
            {invoice.invoiceStatusCode}
            {invoice.status === "voided" && " (pending void)"}
          </div>
        )}

        <div className="rounded-md border bg-muted/30 p-3 text-sm">
          <div className="flex items-center justify-between">
            <span className="font-medium">
              {formatTramsInvoiceDisplayNumber(getTramsInvoiceNumberFromMetadata(invoice.metadata))}
            </span>
            <Badge variant="secondary">
              From: {invoice.recordLocator ?? "n/a"}
            </Badge>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-2">
          <Button
            type="button"
            variant={mode === "new" ? "default" : "outline"}
            aria-pressed={mode === "new"}
            disabled={!isMovable || isSubmitting}
            onClick={() => setMode("new")}
          >
            Create a New Trip
          </Button>
          <Button
            type="button"
            variant={mode === "existing" ? "default" : "outline"}
            aria-pressed={mode === "existing"}
            disabled={!isMovable || isSubmitting}
            onClick={() => setMode("existing")}
          >
            Select Existing Trip
          </Button>
        </div>

        {mode === "existing" && (
          <div className="space-y-3">
            <Label>Selected trip</Label>
            <Popover open={tripPopoverOpen} onOpenChange={setTripPopoverOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  role="combobox"
                  disabled={!isMovable}
                  className={cn(
                    "w-full justify-between font-normal",
                    !selectedTrip && "text-muted-foreground",
                  )}
                >
                  {selectedTrip ? selectedTrip.name : "Search trips..."}
                  <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
              </PopoverTrigger>
              {/* AR-316: 460px overflows a 375px viewport */}
              <PopoverContent
                className="w-[min(460px,calc(100vw-2rem))] p-0"
                align="start"
                collisionPadding={8}
              >
                <Command shouldFilter={false}>
                  <CommandInput
                    placeholder="Search by name or trip #..."
                    value={tripSearch}
                    onValueChange={setTripSearch}
                  />
                  <CommandList>
                    <CommandEmpty>No trips found.</CommandEmpty>
                    <CommandGroup>
                      <ScrollArea className="h-[220px]">
                        {filteredTrips.map((trip) => (
                          <CommandItem
                            key={trip.id}
                            value={String(trip.id)}
                            onSelect={() => {
                              setSelectedTrip(trip);
                              setTripPopoverOpen(false);
                              setTripSearch("");
                            }}
                          >
                            <div className="flex-1">
                              <div className="font-medium">{trip.name}</div>
                              <div className="text-xs text-muted-foreground">
                                {trip.recordLocator
                                  ? `ID: ${trip.recordLocator}`
                                  : "No trip #"}
                              </div>
                            </div>
                            {selectedTrip?.id === trip.id && (
                              <Check className="h-4 w-4 text-primary" />
                            )}
                          </CommandItem>
                        ))}
                      </ScrollArea>
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>

            {selectedTrip && (
              <div className="rounded-md border bg-muted/30 p-3 text-sm">
                <div className="flex items-center justify-between">
                  <span className="font-medium">{selectedTrip.name}</span>
                  <span className="flex items-center gap-2 text-xs text-muted-foreground">
                    <Badge variant="outline">
                      {invoice.recordLocator ?? "n/a"}
                    </Badge>
                    <ArrowRight className="h-3 w-3" />
                    <Badge variant="default">
                      {selectedTrip.recordLocator ?? "n/a"}
                    </Badge>
                  </span>
                </div>
              </div>
            )}
          </div>
        )}

        {mode === "new" && (
          <div className="space-y-3">
            <NewTripFields
              value={newTrip}
              onChange={updateNewTrip}
              disabled={!isMovable || isSubmitting}
              idPrefix="move-invoice-new-trip"
            />
            <p className="text-xs text-muted-foreground">
              A new trip will be created with a new trip #.
            </p>
          </div>
        )}

        <div>
          <Label>Reason (optional)</Label>
          <Textarea
            placeholder="e.g. Moved off Auto-Invoiced to the matching real trip"
            value={reason}
            disabled={!isMovable}
            onChange={(e) => setReason(e.target.value)}
            rows={2}
          />
        </div>

        </div>

        <DialogFooter className="border-t bg-background px-6 py-4">
          <Button
            type="button"
            variant="outline"
            disabled={isSubmitting}
            onClick={() => handleClose(false)}
          >
            Cancel
          </Button>
          <Button
            type="button"
            disabled={
              !isMovable ||
              isSubmitting ||
              (mode === "existing" && !selectedTrip) ||
              (mode === "new" &&
                (!newTrip.name.trim() ||
                  newTrip.destinations.length === 0 ||
                  (newTrip.isGroup && !newTrip.groupName.trim())))
            }
            onClick={handleSubmit}
          >
            {isSubmitting && (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            )}
            Move invoice
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
